<script setup lang="ts">
import type { IHostDashboardFilters } from "./types"
import { onMounted, reactive, watchEffect } from "vue"
import { Drawer, Select } from "@/components/ui"
import { useTranslation } from "@/composables"
import { EventVaultStatus, ProductKey, ProductTypes } from "@/models"
import { HostDashboardFiltersKey } from "@/symbols"

interface HostDashboardFiltersProps {
  filters: IHostDashboardFilters
}

const props = defineProps<HostDashboardFiltersProps>()

const emit = defineEmits<{
  "update:filters": [filters: IHostDashboardFilters]
}>()

const { t } = useTranslation()

const filterDefaults: IHostDashboardFilters = {
  perPage: 10,
  sortBy: undefined,
  orderBy: undefined,
  status: undefined,
  product: undefined,
}

const internalFiltersState = reactive<IHostDashboardFilters>(structuredClone(filterDefaults))

onMounted(() => {
  if (props.filters?.perPage)
    internalFiltersState.perPage = props.filters.perPage
  if (props.filters?.sortBy)
    internalFiltersState.sortBy = props.filters.sortBy
  if (props.filters?.orderBy)
    internalFiltersState.orderBy = props.filters.orderBy
  if (props.filters?.status)
    internalFiltersState.status = props.filters.status
  if (props.filters?.product)
    internalFiltersState.product = props.filters.product
})

const perPageOptions = [
  { label: "5", value: 5 },
  { label: "10", value: 10 },
  { label: "15", value: 15 },
  { label: "25", value: 25 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
]

const sortByOptions = [
  { label: t("title"), value: "title" },
  { label: t("host_dashboard_filters.sort.event_date"), value: "eventDate" },
  { label: t("host_dashboard_filters.sort.close_date"), value: "closeDate" },
]

const orderByOptions = [
  { label: t("ascending"), value: "asc" },
  { label: t("descending"), value: "desc" },
]

const statusOptions = [
  { label: EventVaultStatus.getLabel(EventVaultStatus.Draft), value: EventVaultStatus.Draft },
  { label: EventVaultStatus.getLabel(EventVaultStatus.Ready), value: EventVaultStatus.Ready },
  { label: EventVaultStatus.getLabel(EventVaultStatus.Live), value: EventVaultStatus.Live },
  { label: EventVaultStatus.getLabel(EventVaultStatus.Closed), value: EventVaultStatus.Closed },
  { label: EventVaultStatus.getLabel(EventVaultStatus.Expired), value: EventVaultStatus.Expired },
  { label: EventVaultStatus.getLabel(EventVaultStatus.Archived), value: EventVaultStatus.Archived },
]

const productOptions = [
  { label: ProductTypes.getLabel(ProductKey.Mini), value: "mini" },
  { label: ProductTypes.getLabel(ProductKey.Small), value: "small" },
  { label: ProductTypes.getLabel(ProductKey.Medium), value: "medium" },
  { label: ProductTypes.getLabel(ProductKey.LargeTest), value: "large" },
  { label: ProductTypes.getLabel(ProductKey.XLarge), value: "xlarge" },
  { label: ProductTypes.getLabel(ProductKey.Guests2000), value: "2000Guests" },
  { label: ProductTypes.getLabel(ProductKey.XXLarge), value: "xxlarge" },
  { label: ProductTypes.getLabel(ProductKey.Festival), value: "festival" },
]

watchEffect(() => {
  emit("update:filters", internalFiltersState)
})
</script>

<template>
  <Drawer :name="HostDashboardFiltersKey" :title="t('filters')">
    <form @submit.prevent>
      <div class="flex flex-col gap-4">
        <Select
          v-model="internalFiltersState.perPage" name="perPage"
          :label="t('host_dashboard_filters.per_page_label')" :items="perPageOptions"
        />
        <Select
          v-model="internalFiltersState.sortBy" name="sortBy" :label="t('host_dashboard_filters.sort_by_label')"
          :items="sortByOptions" clearable
        />
        <Select
          v-model="internalFiltersState.orderBy" name="orderBy"
          :label="t('host_dashboard_filters.order_by_label')" :items="orderByOptions" clearable
        />
        <Select
          v-model="internalFiltersState.status" name="status" :label="t('host_dashboard_filters.status_label')"
          :items="statusOptions" clearable
        />
        <Select
          v-model="internalFiltersState.product" name="product"
          :label="t('host_dashboard_filters.product_type_label')" :items="productOptions" clearable
        />
      </div>
    </form>
  </Drawer>
</template>
